import CancelIcon from "@mui/icons-material/Cancel"
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import {
  collection,
  deleteDoc,
  doc,
  getFirestore,
  setDoc,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import { timber_debug, timber_warn } from "../Timber"
import { Buyer } from "../Type"

const BuyerDialog: FunctionComponent<{
  userId: string
  buyer: Buyer
  onClickClose: () => void
}> = ({ userId, buyer, onClickClose }) => {
  const intl = useIntl()

  // Firebase
  const firestore = getFirestore()
  const analytics = getAnalytics()
  const buyersRef = collection(firestore, `/users/${userId}/buyers`)

  const [buyerId, setBuyerId] = useState<string>(buyer.id)
  const [buyerName, setBuyerName] = useState<string>(buyer.name)
  const [buyerAddress, setBuyerAddress] = useState<string>(buyer.address)
  const [buyerPhone, setBuyerPhone] = useState<string>(buyer.phone)
  const [buyerEmail, setBuyerEmail] = useState<string>(buyer.email)
  const [buyerWebsite, setBuyerWebsite] = useState<string>(buyer.website)
  const [buyerInsertDate, setBuyerInsertDate] = useState<number>(
    buyer.insertDate
  )

  useEffect(() => {
    setBuyerId(buyer.id)
    setBuyerName(buyer.name)
    setBuyerAddress(buyer.address)
    setBuyerPhone(buyer.phone)
    setBuyerEmail(buyer.email)
    setBuyerWebsite(buyer.website)
    setBuyerEmail(buyer.email)
    setBuyerInsertDate(buyer.insertDate)
  }, [buyer])

  const saveBuyer = () => {
    timber_debug(`Buyer id: ${buyerId} name: ${buyerName}`)
    if (buyerName === "") {
      timber_warn("Empty buyer name")
      return
    }
    const currentTime = dayjs().valueOf()
    if (buyerId === "") {
      // Saves a new buyer
      const buyerRef = doc(buyersRef)
      setDoc(buyerRef, {
        name: buyerName,
        address: buyerAddress,
        phone: buyerPhone,
        email: buyerEmail,
        website: buyerWebsite,
        insertDate: currentTime,
        lastEditDate: currentTime,
      })
        .then(() => {
          timber_debug("Buyer created successfully")
          onClickClose()
          logEvent(analytics, "web_buyer_created")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
      logEvent(analytics, "web_create_buyer_clicked")
    } else {
      // Saves an edited buyer
      const buyerRef = doc(firestore, buyersRef.path, buyerId)
      setDoc(buyerRef, {
        name: buyerName,
        address: buyerAddress,
        phone: buyerPhone,
        email: buyerEmail,
        website: buyerWebsite,
        insertDate: buyerInsertDate,
        lastEditDate: currentTime,
      } as Buyer)
        .then(() => {
          timber_debug("Buyer edited successfully")
          onClickClose()
          logEvent(analytics, "web_buyer_edited")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
      logEvent(analytics, "web_buyer_edit_error")
    }
  }

  const deleteBuyer = () => {
    logEvent(analytics, "web_delete_buyer_clicked")

    if (buyerId !== "") {
      deleteDoc(doc(firestore, buyersRef.path, buyerId))
        .then(() => {
          timber_debug(`Buyer with id ${buyerId} deleted successfully`)
          onClickClose()
          logEvent(analytics, "web_buyer_deleted")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
    } else {
      timber_warn("Unable to delete buyer: empty buyerId")
    }
  }

  return (
    <div>
      <DialogTitle>
        {buyer.id === "" ? (
          <FormattedMessage id="dialog.title.create.buyer" />
        ) : (
          <FormattedMessage id="dialog.title.edit.buyer" />
        )}
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction={"row"}
          alignItems="center"
          rowSpacing={5}
          marginTop={1}
        >
          {/* Buyer name */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              required
              multiline
              label={intl.formatMessage({
                id: "table.headers.name",
              })}
              value={buyerName}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setBuyerName(event.target.value)
                } else {
                  setBuyerName("")
                }
              }}
            />
          </Grid>

          {/* Buyer address Line One */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              required
              multiline
              label={intl.formatMessage({
                id: "business.profile.address",
              })}
              value={buyerAddress}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setBuyerAddress(event.target.value)
                } else {
                  setBuyerAddress("")
                }
              }}
            />
          </Grid>

          {/* Buyer phone */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage({
                id: "table.headers.phone",
              })}
              value={buyerPhone}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setBuyerPhone(event.target.value)
                } else {
                  setBuyerPhone("")
                }
              }}
            />
          </Grid>

          {/* Buyer email */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage({
                id: "table.headers.email",
              })}
              value={buyerEmail}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setBuyerEmail(event.target.value)
                } else {
                  setBuyerEmail("")
                }
              }}
            />
          </Grid>

          {/* Buyer website */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage({
                id: "table.headers.website",
              })}
              value={buyerWebsite}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setBuyerWebsite(event.target.value)
                } else {
                  setBuyerWebsite("")
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickClose}
          variant="outlined"
          startIcon={<CancelIcon />}
        >
          <FormattedMessage id="dialog.button.cancel" />
        </Button>
        {buyerId !== "" && (
          <Button
            color="error"
            onClick={deleteBuyer}
            variant="outlined"
            startIcon={<DeleteIcon />}
          >
            <FormattedMessage id="dialog.button.delete" />
          </Button>
        )}
        <Button
          onClick={saveBuyer}
          variant="contained"
          style={{ background: Color.Primary }}
          startIcon={<SaveIcon />}
        >
          <FormattedMessage id="dialog.button.save" />
        </Button>
      </DialogActions>
    </div>
  )
}

export default BuyerDialog
